import { useCallback, useState } from 'react';

type ImageMetadata = {
  title: string | null;
  description: string | null;
};

type UseImageMetadataGenerationParams = {
  fetchMetadata: (imageId: string) => Promise<ImageMetadata | null>;
  updateImage: (
    imageId: string,
    data: { title: string | null; description: string | null },
  ) => Promise<void>;
  images: { id: string }[];
  onComplete?: () => void;
  onError?: (error: unknown) => void;
};

const GENERATE_CONCURRENCY = 3;

export const useImageMetadataGeneration = ({
  fetchMetadata,
  updateImage,
  images,
  onComplete,
  onError,
}: UseImageMetadataGenerationParams) => {
  const [loadingMetadataIds, setLoadingMetadataIds] = useState<Set<string>>(
    new Set(),
  );
  const [regeneratingAll, setRegeneratingAll] = useState(false);

  const generateMetadataForImage = useCallback(
    async (
      imageId: string,
      options?: { silent?: boolean },
    ): Promise<unknown> => {
      setLoadingMetadataIds(prev => new Set([...prev, imageId]));
      try {
        const metadata = await fetchMetadata(imageId);
        if (metadata?.title || metadata?.description) {
          await updateImage(imageId, {
            title: metadata.title,
            description: metadata.description,
          });
        }
        return null;
      } catch (error) {
        if (!options?.silent) {
          onError?.(error);
        }
        return error;
      } finally {
        setLoadingMetadataIds(prev => {
          const next = new Set(prev);
          next.delete(imageId);
          return next;
        });
      }
    },
    [fetchMetadata, updateImage, onError],
  );

  const regenerateAllMetadata = useCallback(async () => {
    if (images.length === 0) {
      return;
    }

    setRegeneratingAll(true);
    try {
      const queue = images.map(image => image.id);
      let lastError: unknown = null;
      const runWorker = async () => {
        for (let id = queue.shift(); id != null; id = queue.shift()) {
          const error = await generateMetadataForImage(id, { silent: true });
          if (error != null) {
            lastError = error;
          }
        }
      };
      const workerCount = Math.min(GENERATE_CONCURRENCY, queue.length);
      await Promise.all(Array.from({ length: workerCount }, runWorker));
      if (lastError != null) {
        onError?.(lastError);
      }
      onComplete?.();
    } finally {
      setRegeneratingAll(false);
    }
  }, [images, generateMetadataForImage, onComplete, onError]);

  return {
    loadingMetadataIds,
    regeneratingAll,
    generateMetadataForImage,
    regenerateAllMetadata,
  };
};
